import pclnExperimentation from '@pcln/experimentation'
import { useFeature } from '@pcln/feature-components'
import { SegmentedChipGroup } from '@pcln/horizon'
import type {
  HotelFormState,
  HotelSearchFormApi,
  LocationSearchType,
  MultiHotelFormState,
} from '@pcln/horizon-search-form'
import {
  ConnectedAutocompleteSearchField,
  HotelSearchForm,
  MultiHotelSearchForm,
} from '@pcln/horizon-search-form'
import config from 'isomorphic-config'
import { useCallback, useContext, useEffect, useMemo, useRef, useState, type SetStateAction } from 'react'
import Sticky from 'react-stickynode'

import StickyEffect from '@/components/StickyEffect'
import fireMaxRoomsEvent from '@/components/TravelerSelection/ga4'
import { useHotelNeighbourhoodNesting } from '@/components/TypeAhead/useHotelNeighbourhoodNesting'
import { SearchFormContext } from '@/context/SearchFormContext'
import type { GA4PageNameType } from '@/ga4types'
import useSeti from '@/hooks/useSeti'
import logSearchFormAnalytics from '@/shared-utils/log-search-form-analytics'
import type { LOCATION_SEARCH_TYPE } from '@/types'

import { fireBundleAndSaveEvent, fireMultiHotelRadioClick } from '../ga4'
import { handleSubmitForm as handleMultiHotelSubmitForm } from '../SearchForm/MultiHotelSearchForm/utils'
import { groupDealsURL } from '../SearchForm/utils'
import type {
  HOTEL_FORM_PREFILL_ANALYTICS,
  HOTEL_FORM_STATE_TYPE,
  HOTEL_FORM_SUBMIT_TYPE,
  SelectedTopFilters,
  SEM_SEARCH_TYPE,
} from '../types'
import { AI_SEARCH_MODE, SEARCH_MODE_OPTIONS, STANDARD_SEARCH_MODE } from './aiSearchConstants'
import { AiSearchForm, type AiSearchSubmitParams } from './AiSearchForm'
import { submitAiInterpretedSearch } from './aiSearchSubmit'
import FormTypeRadios from './HorizonFormTypeRadio'
import HorizonSelectedFilterChipBar from './HorizonSelectedFilterChipBar'
import { logAiSearchModeToggle, logAiSearchNoRecommendationsSwitch } from './logAiSearch'
import { applyPetFriendlyToTopFilters, PET_FRIENDLY_TOGGLE_SETI } from './petFriendlyAmenity'
import { getDestinationCityId, isSameOccupancy, type LastSearchPrefill } from './prefillFromLastSearch'
import useHotelFormInitialValues from './useHotelFormInitialValues'
import { useLastSearchPrefill } from './useLastSearchPrefill'

/** SETI: hide Single/Multi radios; always show Add Another Hotel on the single form. */
export const ADD_ANOTHER_HOTEL_SETI = 'REARRANGE_HOMEPAGE_SEARCH_FORM_DESKTOP'
export const PRICE_FILTER_SETI = 'PRICE_FILTERS_HOMEPAGE_SEARCH_DESKTOP_MWEB'

type FormType = {
  onSubmit: (data: HOTEL_FORM_SUBMIT_TYPE, pageName: GA4PageNameType) => void
  setLocation?: (city?: LOCATION_SEARCH_TYPE) => void
  showHotelTypeButtons?: boolean
  pageName?: 'homepage' | 'landing'
  isExpressDeal?: boolean
}

/** SETI: pre-fill the homepage hotel form (destination, dates, travellers) from the user's last search. */
export const LAST_SEARCH_PREFILL_SETI = 'PREFILL_HOTEL_SEARCH_FORM_DESKTOP_MWEB'

// Experiment: prefill (pre-select) the destination field with the
// user's last searched city instead of leaving it empty, on desktop and mweb. Only the single-hotel
// form's destination ("Where to?") field is in scope — its searchKey is the
// bare 'endLocation'. The multi-hotel form's per-destination fields
// (e.g. 'hotels.0.endLocation') are deliberately excluded: prefilling every
// empty destination with the same last-searched city would let a multi-hotel
// search submit duplicate destinations.
export function getEnablePrefillFromLastSearch({
  searchKey,
  isPrefillVariant,
}: {
  searchKey: string
  isPrefillVariant: boolean
}) {
  return searchKey === 'endLocation' && isPrefillVariant
}

// Switching to multi-hotel mode carries the single-hotel form's current
// destination into hotel #1's initial value (multiHotelInitialData below) —
// correct when the user actually chose that destination, but wrong when it
// only got there via the passive prefill: that would silently reproduce the
// "prefill every destination" problem multi-hotel is meant to be excluded
// from, just one hop removed (through the single-hotel form instead of
// AutocompleteWrapper's own prefill effect).
export function resolveMultiHotelInitialEndLocation<T>({
  endLocation,
  endLocationWasPrefilled,
}: {
  endLocation: T | null | undefined
  endLocationWasPrefilled: boolean
}): T | null {
  return endLocationWasPrefilled ? null : (endLocation ?? null)
}

/**
 * Analytics-only context for the submit `search` event. Absent unless something was pre-filled, so
 * a form nobody pre-filled reports exactly as it always has.
 */
export function getPrefillAnalytics({
  isDestinationPrefilled,
  appliedPrefill,
}: {
  isDestinationPrefilled: boolean
  appliedPrefill: LastSearchPrefill | null
}): { prefillAnalytics: HOTEL_FORM_PREFILL_ANALYTICS } | Record<string, never> {
  if (!isDestinationPrefilled && !appliedPrefill) return {}

  // Fields that weren't pre-filled stay measured against the form's defaults.
  return { prefillAnalytics: { isDestinationPrefilled, baseline: appliedPrefill ?? {} } }
}

export default function HorizonHotelForm({
  onSubmit,
  setLocation = () => null,
  showHotelTypeButtons = false,
  pageName = 'homepage',
  isExpressDeal = false,
}: FormType) {
  const { initialValues, isMobile, countryCode, cguid, SEMSearch, SEMSEARCH, refId, refClickId } =
    useHotelFormInitialValues()
  const { updateFormValues } = useContext(SearchFormContext)
  const useV2Typeahead = useFeature('siteExperience.useV2Typeahead') ?? false
  const typeAheadEndpointHotels = useV2Typeahead ? 'hotelsV2' : 'hotels'

  const [hotelType, setHotelType] = useState('single')
  const [isStickyInEffect, setIsStickyInEffect] = useState(false)
  const [hotelTripType, setHotelTripType] = useState('STAY')
  const [searchMode, setSearchMode] = useState<string>(STANDARD_SEARCH_MODE)
  const isHomepage = pageName === 'homepage'
  const isMultiHotel = hotelType === 'multi' && showHotelTypeButtons
  // WEB_HTL_SEARCH_SCREEN_NBH_NESTING — the hook owns the VARIANT check, the hotelsV2 requirement
  // and the desktop-only gate. Multi-hotel always opts in; single-hotel only for a plain stay.
  const neighbourhoodNestingEnabled = useHotelNeighbourhoodNesting({
    typeAheadEndpoint: typeAheadEndpointHotels,
    enabled: isMultiHotel || hotelTripType === 'STAY',
  })

  const aiSearchModeVariant = useSeti({
    experimentCode: 'AI_SEARCH_IN_YOUR_OWN_WORDS',
    shouldFireImpression: isHomepage,
  })
  const showSearchModeChips = isHomepage && aiSearchModeVariant === 'VARIANT'
  const isAiSearchMode = showSearchModeChips && searchMode === AI_SEARCH_MODE

  const canShowAddAnotherHotelEntry = showHotelTypeButtons && !isExpressDeal && !isAiSearchMode
  const addAnotherHotelVariant = useSeti({
    experimentCode: ADD_ANOTHER_HOTEL_SETI,
    shouldFireImpression: canShowAddAnotherHotelEntry,
  })
  const isAddAnotherHotelVariant = canShowAddAnotherHotelEntry && addAnotherHotelVariant === 'VARIANT'

  const enableStickyEffect =
    !isExpressDeal && !isMobile && pageName === 'homepage' && hotelType === 'single' && !isAiSearchMode

  const isAiSearchEnabled = !isExpressDeal && isMobile && hotelType === 'single' && hotelTripType === 'STAY'

  const aiSearchVariant = useSeti({
    experimentCode: 'USB_SEARCH_AI_QUERIES',
    shouldFireImpression: isAiSearchEnabled,
  })

  const isLTRModelEnabled = !isExpressDeal && !isMobile && hotelTripType === 'STAY'

  const ltrModelVariant = useSeti({
    experimentCode: 'USB_SEARCH_LTR_MODEL',
    shouldFireImpression: isLTRModelEnabled,
  })

  const hasMobileLTRSearch = !isExpressDeal && isMobile && hotelTripType === 'STAY'

  const hasLTRSearch = (ltrModelVariant === 'VARIANT' && isLTRModelEnabled) || hasMobileLTRSearch

  const hasAISearch =
    (aiSearchVariant === 'VARIANT_1' || aiSearchVariant === 'VARIANT_2') && isAiSearchEnabled

  const handleStickyEffectStateChange = ({ status }: Sticky.Status) => {
    if (status === Sticky.STATUS_FIXED && !isStickyInEffect) {
      if (enableStickyEffect) {
        setIsStickyInEffect(true)
      }
    } else if (isStickyInEffect) {
      setIsStickyInEffect(false)
    }
  }

  const isPartnersURL =
    useSeti({
      experimentCode: 'HP_LP_HOTELS_TEN_ROOMS',
      shouldFireImpression: false,
    }) === 'VARIANT'
  const removeBackground =
    useSeti({
      experimentCode: 'HP_SEARCH_FORM_HORIZON_CROSS_SELL_DESKTOP',
      shouldFireImpression: !isExpressDeal,
    }) === 'VARIANT'

  const isPackageCheckboxHighlightEnabled =
    useSeti({
      experimentCode: 'PKG_MWEB_CHECKBOX_HIGHLIGHT',
      shouldFireImpression: isMobile && !isExpressDeal,
    }) === 'VARIANT' && isMobile

  const aiSearchButtonVariant = useSeti({
    experimentCode: 'USB_AI_SEARCH_BUTTON_HP',
    shouldFireImpression: isHomepage,
  })
  const showAiSearchButton =
    isHomepage && (aiSearchButtonVariant === 'VARIANT1' || aiSearchButtonVariant === 'VARIANT2')

  const recentSearchImprovementVariant = useSeti({
    experimentCode: 'HTL_SEARCH_FRM_RECENT_SEARCH_IMPRV',
    shouldFireImpression: false,
  })
  const mergeRecentAndRecommendedSearches = recentSearchImprovementVariant === 'VARIANT1'
  const preferRecentSearchesOnly = recentSearchImprovementVariant === 'VARIANT2'

  const recentSearchLogicUpdateVariant = useSeti({
    experimentCode: 'DESK_MWEB_HTL_RECENT_SEARCH_LOGIC_UPDATE',
    shouldFireImpression: false,
  })
  const mergeAbandonedViewedAndRecentSearches = recentSearchLogicUpdateVariant === 'VARIANT'

  // Pre-fills the homepage form from the user's last search: the "Where to?" field, dates and
  // travellers, on desktop and mweb. The impression is fired manually once a
  // pre-fill is actually available — see the last-search effect below — not on render.
  const isPrefillVariant =
    useSeti({
      experimentCode: LAST_SEARCH_PREFILL_SETI,
      shouldFireImpression: false,
    }) === 'VARIANT'

  const isPriceFilterEligible = isHomepage && !isExpressDeal && !isMultiHotel && !isAiSearchMode
  const showPriceFilter =
    useSeti({
      experimentCode: PRICE_FILTER_SETI,
      shouldFireImpression: isPriceFilterEligible,
    }) === 'VARIANT' && isPriceFilterEligible

  const isPetFriendlyToggleEligible = !isExpressDeal && hotelTripType === 'STAY'
  const showPetFriendlyToggle =
    useSeti({
      experimentCode: PET_FRIENDLY_TOGGLE_SETI,
      shouldFireImpression: isPetFriendlyToggleEligible,
    }) === 'VARIANT' && isPetFriendlyToggleEligible

  const fireRecentSearchImprovementImpression = useCallback(() => {
    pclnExperimentation.fireImpression('HTL_SEARCH_FRM_RECENT_SEARCH_IMPRV')
  }, [])

  const fireRecentSearchLogicUpdateImpression = useCallback(() => {
    pclnExperimentation.fireImpression('DESK_MWEB_HTL_RECENT_SEARCH_LOGIC_UPDATE')
  }, [])

  // Captures the typeahead `select_content` item_name for the selected destination
  // so the submit `search` event can carry the same value.
  const searchItemNameRef = useRef<string | null>(null)

  // Tracks whether trackedValues.endLocation currently holds a passive prefill
  // rather than a real user pick, so multiHotelInitialData (below) can exclude
  // it — otherwise switching to multi-hotel mode would carry the single-hotel
  // form's auto-prefilled destination into hotel #1 as if the user had chosen
  // it themselves, which is exactly the "prefill every destination" problem
  // the multi-hotel form is supposed to be excluded from.
  const [endLocationWasPrefilled, setEndLocationWasPrefilled] = useState(false)
  // Values carried back from the last remaining multi-hotel row so the remounted
  // single form restores destination, dates, and travelers in the default layout.
  const [returnedSingleValues, setReturnedSingleValues] = useState<Partial<HotelFormState> | null>(null)

  const [trackedValues, setTrackedValues] = useState<Partial<HotelFormState>>({
    endLocation: initialValues.endLocation as LocationSearchType | null,
    startDate: initialValues.startDate,
    endDate: initialValues.endDate,
    travelers: initialValues.travelers,
  })

  const hotelPlannerLink = groupDealsURL({
    location: trackedValues.endLocation?.itemName ?? '',
    startDate: trackedValues.startDate ?? '',
    endDate: trackedValues.endDate ?? '',
    isPartnersURL,
  })

  const multiHotelInitialData = useMemo(
    () => ({
      endLocation: resolveMultiHotelInitialEndLocation({
        endLocation: trackedValues.endLocation,
        endLocationWasPrefilled,
      }),
      startDate: trackedValues.startDate ?? '',
      endDate: trackedValues.endDate ?? '',
      travelers: trackedValues.travelers ?? initialValues.travelers,
    }),
    [
      endLocationWasPrefilled,
      trackedValues.endLocation,
      trackedValues.startDate,
      trackedValues.endDate,
      trackedValues.travelers,
      initialValues.travelers,
    ],
  )

  // Dates + travellers from the user's last search (PREFILL_HOTEL_SEARCH_FORM_DESKTOP_MWEB). The data arrives
  // after the form has mounted, so it is applied once through the form's API — and only while the
  // user hasn't touched these fields, so a slow response never overwrites their input.
  //
  // The impression fires for control and variant alike, at the moment a pre-fill is available to
  // show, so both buckets are counted on the same population; only the variant is given the values.
  //
  // The dates and travellers belong to the same city the destination field pre-fills: the
  // autocomplete reports that city (for control too, which isn't given the destination), and the
  // search for it is looked up in the user's recent searches.
  const [lastSearchedDestination, setLastSearchedDestination] = useState<LocationSearchType | null>(null)
  const { prefill: lastSearchPrefill } = useLastSearchPrefill(lastSearchedDestination)
  const hotelFormApiRef = useRef<HotelSearchFormApi>(null)
  const didHandleLastSearchPrefillRef = useRef(false)
  // What the variant was actually given, for the submit event to measure changes against.
  const appliedLastSearchPrefillRef = useRef<LastSearchPrefill | null>(null)
  // The AI-search toggle unmounts the standard form, which comes back from its initial values. What
  // the variant was given is carried into them so the dates and travellers (and the analytics
  // baseline measured against them) still match what the form shows.
  const [prefillToRestore, setPrefillToRestore] = useState<LastSearchPrefill | null>(null)
  const isLastSearchPrefillSurface = isHomepage && !isExpressDeal
  const areDatesAndTravelersUntouched =
    trackedValues.startDate === initialValues.startDate &&
    trackedValues.endDate === initialValues.endDate &&
    isSameOccupancy(trackedValues.travelers, initialValues.travelers)
  // The last search is for the city the autocomplete reports, not necessarily the one in the form:
  // the user may have picked another, or the form may have started with a destination. Dates for
  // one city must never be shown next to another, so only an empty or still-pre-filled destination
  // qualifies. Control users (who are not given the destination) are held to the same rule.
  const currentDestinationCityId = getDestinationCityId(trackedValues.endLocation)
  const isDestinationLastSearched =
    currentDestinationCityId === null ||
    currentDestinationCityId === getDestinationCityId(lastSearchedDestination)

  useEffect(() => {
    if (
      didHandleLastSearchPrefillRef.current ||
      !isLastSearchPrefillSurface ||
      !lastSearchPrefill ||
      isMultiHotel ||
      isAiSearchMode ||
      !areDatesAndTravelersUntouched ||
      !isDestinationLastSearched ||
      !hotelFormApiRef.current
    ) {
      return
    }
    didHandleLastSearchPrefillRef.current = true
    if (isPrefillVariant) {
      hotelFormApiRef.current.setSearchValues(lastSearchPrefill)
      appliedLastSearchPrefillRef.current = lastSearchPrefill
    }
    pclnExperimentation.fireImpression(LAST_SEARCH_PREFILL_SETI)
  }, [
    isLastSearchPrefillSurface,
    isPrefillVariant,
    lastSearchPrefill,
    isMultiHotel,
    isAiSearchMode,
    areDatesAndTravelersUntouched,
    isDestinationLastSearched,
  ])

  const singleFormInitialValues = useMemo(
    () =>
      ({
        ...(initialValues as unknown as Partial<HotelFormState>),
        ...(prefillToRestore ?? {}),
        ...(returnedSingleValues ?? {}),
      }) as Partial<HotelFormState>,
    [initialValues, prefillToRestore, returnedSingleValues],
  )

  const handleReturnToSingleHotel = useCallback(
    (values: {
      endLocation: LocationSearchType | null
      startDate: string
      endDate: string
      travelers: HotelFormState['travelers']
    }) => {
      const next = {
        endLocation: values.endLocation,
        startDate: values.startDate,
        endDate: values.endDate,
        travelers: values.travelers,
      }
      setReturnedSingleValues(next)
      setTrackedValues((prev) => ({ ...prev, ...next }))
      setEndLocationWasPrefilled(false)
      updateFormValues?.({ ...trackedValues, ...next } as unknown as HOTEL_FORM_STATE_TYPE)
      setHotelType('single')
    },
    [trackedValues, updateFormValues],
  )

  const handleFormValuesChange = useCallback(
    (values: HotelFormState) => {
      setTrackedValues(values)
      updateFormValues?.(values as unknown as HOTEL_FORM_STATE_TYPE)
    },
    [updateFormValues],
  )

  const handleSubmit = useCallback(
    (data: HotelFormState) => {
      if (isMobile && typeof window.PCLN?.pclnEventDispatcher?.dispatch === 'function') {
        if (isHomepage) {
          window.PCLN.pclnEventDispatcher.dispatch('FIND YOUR HOTEL')
        }
        window.PCLN.pclnEventDispatcher.dispatch('FIND_YOUR_HOTEL_DATA', {
          cityName: data.endLocation?.cityName,
          cityId: data.endLocation?.cityID,
          checkInDate: data.startDate,
          checkOutDate: data.endDate,
        })
      }

      let userLatitude: number | undefined
      let userLongitude: number | undefined
      if (data.endLocation?.type === 'GEO' && data.endLocation?.lat && data.endLocation?.lon) {
        userLatitude = data.endLocation.lat
        userLongitude = data.endLocation.lon
      }

      const { petFriendly, selectedTopFilters, ...rest } = data
      const formValues = {
        ...rest,
        selectedTopFilters: applyPetFriendlyToTopFilters({
          selectedTopFilters,
          petFriendly: petFriendly && data.tripType === 'STAY',
        }),
        ...(SEMSearch && { SEMSearch: SEMSearch as SEM_SEARCH_TYPE }),
        ...(SEMSEARCH && { SEMSEARCH: SEMSEARCH as SEM_SEARCH_TYPE }),
        ...(refId && { refId }),
        ...(refClickId && { refClickId }),
        useCurrentLocation: data.endLocation?.type === 'GEO',
        ...(userLatitude && userLongitude && { userLatitude, userLongitude }),
        ...(searchItemNameRef.current ? { searchItemName: searchItemNameRef.current } : {}),
        ...getPrefillAnalytics({
          isDestinationPrefilled: endLocationWasPrefilled,
          appliedPrefill: appliedLastSearchPrefillRef.current,
        }),
      }

      onSubmit(formValues as unknown as HOTEL_FORM_SUBMIT_TYPE, pageName)
      logSearchFormAnalytics({
        cguid,
        pageName,
        productType: 'hotels',
        isMobile,
      })
    },
    [
      isMobile,
      isHomepage,
      SEMSearch,
      SEMSEARCH,
      refId,
      refClickId,
      onSubmit,
      pageName,
      cguid,
      endLocationWasPrefilled,
    ],
  )

  const handleMultiHotelSubmit = useCallback(
    (data: MultiHotelFormState) => {
      handleMultiHotelSubmitForm(data)
      logSearchFormAnalytics({
        cguid,
        pageName,
        productType: 'multi-hotel',
        isMobile,
      })
    },
    [cguid, isMobile, pageName],
  )

  const handleAiSearchSubmit = useCallback(
    (params: AiSearchSubmitParams) => {
      submitAiInterpretedSearch({
        search: params,
        onHotelSubmit: (values) => {
          handleSubmit(values as unknown as HotelFormState)
        },
        baseValues: initialValues,
        pageName,
      })
    },
    [handleSubmit, initialValues, pageName],
  )

  const switchToMultiHotel = useCallback(() => {
    if (hasAISearch) {
      setTrackedValues((prev) => ({ ...prev, endLocation: null }))
    }
    setHotelType('multi')
    fireMultiHotelRadioClick(pageName)
  }, [hasAISearch, pageName])

  const renderStandardForm = () => (
    <>
      {showHotelTypeButtons && !isAddAnotherHotelVariant && (
        <FormTypeRadios
          formType={hotelType}
          onChange={(selectedFormName) => {
            if (hasAISearch && selectedFormName === 'multi') {
              setTrackedValues((prev) => ({ ...prev, endLocation: null }))
            }
            // Like the AI-search toggle, switching to multi-hotel unmounts the single form, which
            // comes back from its initial values — carry the pre-fill into them.
            if (selectedFormName === 'multi') setPrefillToRestore(appliedLastSearchPrefillRef.current)
            setHotelType(selectedFormName)
            fireMultiHotelRadioClick(pageName)
          }}
        />
      )}
      <StickyEffect
        enabled={enableStickyEffect}
        isStickyInEffect={isStickyInEffect}
        handleStickyEffectStateChange={handleStickyEffectStateChange}
        innerZ={isMobile ? 0 : 3}
      >
        {isMultiHotel ? (
          <MultiHotelSearchForm
            initialData={multiHotelInitialData}
            isMobile={isMobile}
            autoAdvance={!isMobile}
            typeAheadEndpoint={typeAheadEndpointHotels}
            onSubmit={handleMultiHotelSubmit}
            onReturnToSingleHotel={isAddAnotherHotelVariant ? handleReturnToSingleHotel : undefined}
            autocompleteOptions={({ searchKey }) => ({
              isMobile,
              mergeRecentAndRecommendedSearches,
              preferRecentSearchesOnly,
              onRecentSearchImprovementImpression: fireRecentSearchImprovementImpression,
              mergeAbandonedViewedAndRecentSearches,
              onRecentSearchLogicUpdateImpression: fireRecentSearchLogicUpdateImpression,
              neighbourhoodNestingEnabled,
              hasLTRSearch,
              enablePrefillFromLastSearch: getEnablePrefillFromLastSearch({
                searchKey,
                isPrefillVariant,
              }),
            })}
          />
        ) : (
          <HotelSearchForm
            apiRef={hotelFormApiRef}
            initialValues={singleFormInitialValues}
            isMobile={isMobile}
            autoAdvance={!isMobile}
            typeAheadEndpoint={typeAheadEndpointHotels}
            setLocation={setLocation}
            isStickyInEffect={isStickyInEffect}
            hotelPlannerLinkHref={hotelPlannerLink}
            removeBackground={isExpressDeal || removeBackground}
            highlightBundleAndSave={isPackageCheckboxHighlightEnabled}
            showBundleAndSave={!isExpressDeal}
            showCurrentLocation={!isExpressDeal && isMobile}
            onMaxRoomsClick={() => {
              pclnExperimentation.fireImpression('HP_LP_HOTELS_TEN_ROOMS')
              window.open(hotelPlannerLink, '_blank')
              fireMaxRoomsEvent(pageName, 'hotel')
            }}
            onBundleChange={(tripType, checked) => {
              setHotelTripType(tripType)
              if (checked) {
                try {
                  fireBundleAndSaveEvent(tripType)
                } catch {
                  // GA4TripTypeMapper may throw on unexpected trip type combinations
                }
              }
            }}
            onFormValuesChange={handleFormValuesChange}
            onSubmit={handleSubmit}
            submitButtonFixedOnMobile={!isExpressDeal}
            showPetFriendlyToggle={showPetFriendlyToggle}
            showPriceFilter={showPriceFilter}
            priceFilterProps={{ ...config.client.hotels.priceGuidance, countryCode }}
            onAddAnotherHotel={isAddAnotherHotelVariant ? switchToMultiHotel : undefined}
            autocompleteOptions={({ searchKey }) => ({
              isMobile,
              showFilterChips: !isExpressDeal,
              aiSearchButtonVariant: showAiSearchButton ? aiSearchButtonVariant : undefined,
              mergeRecentAndRecommendedSearches,
              preferRecentSearchesOnly,
              onRecentSearchImprovementImpression: fireRecentSearchImprovementImpression,
              mergeAbandonedViewedAndRecentSearches,
              onRecentSearchLogicUpdateImpression: fireRecentSearchLogicUpdateImpression,
              neighbourhoodNestingEnabled,
              hasAISearch,
              hasLTRSearch,
              enablePrefillFromLastSearch: getEnablePrefillFromLastSearch({
                searchKey,
                isPrefillVariant,
              }),
              // Only the destination's selection feeds the submit `search` event's item_name; the
              // Bundle & Save departure airport shares this form but not that analytics field.
              onSelectItemName:
                searchKey === 'endLocation'
                  ? (name) => {
                      searchItemNameRef.current = name
                    }
                  : undefined,
              // Reported for control too, so both buckets pick the dates for the same city.
              onLastSearchedItemChange: searchKey === 'endLocation' ? setLastSearchedDestination : undefined,
            })}
            onLocationSelect={(searchKey, _item, context) => {
              // A real pick on the destination supersedes an earlier prefill; picking the
              // departure airport must not clear the flag while the destination is still prefilled.
              if (searchKey === 'endLocation') {
                setEndLocationWasPrefilled(Boolean(context?.isPrefill))
              }
            }}
            renderSelectedFilterChipBar={
              isExpressDeal
                ? undefined
                : (chipBarProps) => (
                    <HorizonSelectedFilterChipBar
                      selectedFilterChipData={
                        chipBarProps.selectedFilterChipData as unknown as SelectedTopFilters
                      }
                      setSelectedFilterChipData={
                        chipBarProps.setSelectedFilterChipData as unknown as (
                          value: SetStateAction<SelectedTopFilters>,
                        ) => void
                      }
                    />
                  )
            }
          />
        )}
      </StickyEffect>
    </>
  )

  if (showSearchModeChips) {
    // Mobile drops the leading icons so the label and Beta badge fit the full-width track.
    const searchModeOptions = isMobile
      ? SEARCH_MODE_OPTIONS.map((option) => ({ ...option, iconLeft: undefined, customIcon: undefined }))
      : SEARCH_MODE_OPTIONS

    return (
      <div className='@container flex w-auto flex-col gap-3'>
        <div className={isMobile ? 'w-full' : undefined}>
          <SegmentedChipGroup
            name='search-mode'
            options={searchModeOptions}
            value={searchMode}
            handleChange={(value: unknown) => {
              const nextMode = String(value)
              if (nextMode === searchMode) return
              logAiSearchModeToggle({ toMode: nextMode, pageName })
              if (nextMode === AI_SEARCH_MODE) setPrefillToRestore(appliedLastSearchPrefillRef.current)
              setSearchMode(nextMode)
            }}
            aria-label='Search mode'
            fullWidth={isMobile}
          />
        </div>
        {isAiSearchMode ? (
          <AiSearchForm
            isMobile={isMobile}
            initialStartDate={initialValues.startDate}
            initialEndDate={initialValues.endDate}
            initialTravelers={initialValues.travelers}
            onSubmitQuery={handleAiSearchSubmit}
            onSwitchToStandardSearch={(searchQuery) => {
              logAiSearchNoRecommendationsSwitch(searchQuery)
              setSearchMode(STANDARD_SEARCH_MODE)
            }}
            renderCityAutocomplete={(props) => (
              // The AI-search city chip renders a hotel typeahead in place. Recents and
              // recommendations belong to the standard search form and would look out of place
              // inside the chip — `hideRecentsAndRecommendations` suppresses both rows.
              <ConnectedAutocompleteSearchField
                ref={props.inputRef}
                searchKey={props.searchKey}
                label={props.label}
                // Prefer the form's V2-aware endpoint over the chip's hardcoded `'hotels'` —
                // neighbourhood nesting only runs on hotelsV2, so the chip must match the
                // standard search form when `siteExperience.useV2Typeahead` is on.
                typeAheadEndpoint={typeAheadEndpointHotels}
                errorMessage={props.errorMessage}
                initialSelectedItem={props.initialSelectedItem}
                onItemSelect={(item, context) => {
                  if (context?.isPrefill) return
                  props.onItemSelect(item as LocationSearchType | null)
                }}
                isMobile={isMobile}
                neighbourhoodNestingEnabled={neighbourhoodNestingEnabled}
                hasLTRSearch={hasLTRSearch}
                hideRecentsAndRecommendations
                renderTrigger={props.renderTrigger}
              />
            )}
          />
        ) : (
          renderStandardForm()
        )}
      </div>
    )
  }

  return <div className='flex w-auto flex-col gap-3'>{renderStandardForm()}</div>
}
